import { STORAGE_KEYS, SUBSCRIPTION_TYPES, DISCOUNT_DISTRIBUTION } from "../const";
import { roundOff } from "./NumberFormatHelper";
import validityExpired from "../components/utils/validityExpired";
import ShopService from "services/ShopService";

function saveSelectedShopDetails(shop) {
  localStorage.setItem(STORAGE_KEYS.SHOP_ID, shop.id);
  localStorage.setItem(STORAGE_KEYS.SHOP_NAME, shop.shop_name);
  localStorage.setItem(STORAGE_KEYS.USER_TYPE, shop.subscription.type);
}

function getSubscriptionRemainingDays(shop) {
  if (!shop) return;
  const subscriptionEndDate = new Date(shop.subscription_end_date);
  const milliSecondsPerDay = 1000 * 60 * 60 * 24;
  const subscriptionRemainingDays = (subscriptionEndDate.getTime() - new Date().getTime()) / milliSecondsPerDay;
  return Math.round(subscriptionRemainingDays).toFixed(0);
}

function getAmountDecimalCount(shop) {
  return shop?.tax_region?.toLowerCase() === "oman" ? 3 : 2;
}

function getAmountFormatted(shop, amount) {
  return roundOff(amount, getAmountDecimalCount(shop));
}

function getBankDetails() {
  const storedDetails = localStorage.getItem(STORAGE_KEYS.BANK_DETAILS);
  if (storedDetails) {
    try {
      return JSON.parse(storedDetails);
    } catch (error) {
      console.error("Error parsing bank details from localStorage", error);
      return null;
    }
  }
  return null;
}

function saveBankDetails(bankDetails) {
  localStorage.setItem(STORAGE_KEYS.BANK_DETAILS, JSON.stringify(bankDetails));
}
export function getCachedShopProfile() {
  const cached = localStorage.getItem(STORAGE_KEYS.SHOP_PROFILE);
  if (!cached) return null;

  try {
    return JSON.parse(cached);
  } catch (error) {
    console.error("Failed to parse cached shop profile:", error);
    localStorage.removeItem(STORAGE_KEYS.SHOP_PROFILE);
    return null;
  }
}

export function getPaymentModes() {
  const modes = localStorage.getItem(STORAGE_KEYS.PAYMENT_MODES);
  if (!modes) return null;

  try {
    return JSON.parse(modes);
  } catch (error) {
    console.error("Failed to parse payment modes:", error);
    return null;
  }
}

function hasFullKotValidity(shop) {
  if (!shop) return false;
  return !validityExpired(shop.kot_validity);
}

function hasKotLiteValidity(shop) {
  if (!shop) return false;
  return !validityExpired(shop.kot_lite_validity);
}

function hasKotValidity(shop) {
  if (!shop) return false;
  return hasFullKotValidity(shop) || hasKotLiteValidity(shop);
}

function hasLabelScaleValidity(shop) {
  if (!shop) return false;
  return !validityExpired(shop.smart_scale_validity);
}

function isPremiumShop(shop) {
  if (!shop) return false;
  return shop.subscription?.type === SUBSCRIPTION_TYPES.PREMIUM;
}

function showOrderStatus(shop) {
  if (!shop) return false;
  const hasKitchenDisplay = !validityExpired(shop.kitchen_display_validity);
  const hasStatusDisplay = !validityExpired(shop.status_display_validity);
  return hasKitchenDisplay || hasStatusDisplay;
}

export async function saveMachineToShopProfileConfig(machineData, machineToEdit) {
  try {
    const currentProfile = getCachedShopProfile() || (await ShopService.loadShopProfile()) || {};
    const currentConfig = (currentProfile && currentProfile.config) || {};
    const currentLabelScales = Array.isArray(currentConfig.label_scales) ? [...currentConfig.label_scales] : [];

    const newScaleEntry = {
      billPrefix: "",
      connectedInterfacePort: "",
      connectedPort: "",
      connectedTo: "",
      isSupportSales: false,
      isSupportUnicode: false,
      macId: machineData.mac_id,
      machineName: machineData.machine_name,
      machineNumber: machineData.machine_number,
    };

    const targetMacId = (machineToEdit?.mac_id || machineData.mac_id || "").toUpperCase();
    const existingIndex = currentLabelScales.findIndex((scale) => (scale.macId || "").toUpperCase() === targetMacId);

    if (existingIndex !== -1) {
      currentLabelScales[existingIndex] = {
        ...currentLabelScales[existingIndex],
        ...newScaleEntry,
      };
    } else {
      currentLabelScales.push(newScaleEntry);
    }

    const updatedConfig = {
      ...currentConfig,
      label_scales: currentLabelScales,
    };

    const updatedProfile = {
      ...currentProfile,
      config: updatedConfig,
    };

    localStorage.setItem(STORAGE_KEYS.SHOP_PROFILE, JSON.stringify(updatedProfile));

    const resp = await ShopService.updateShopProfile({ config: updatedConfig });
    if (resp && typeof resp === "object") {
      localStorage.setItem(STORAGE_KEYS.SHOP_PROFILE, JSON.stringify(resp));
    }
  } catch (error) {
    console.error("Failed to save machine into shop profile config:", error);
  }
}

export async function removeMachineFromShopProfileConfig(macId) {
  try {
    const currentProfile = getCachedShopProfile() || (await ShopService.loadShopProfile()) || {};
    const currentConfig = (currentProfile && currentProfile.config) || {};
    const currentLabelScales = Array.isArray(currentConfig.label_scales) ? [...currentConfig.label_scales] : [];

    const targetMacId = (macId || "").trim().toUpperCase();
    const filteredScales = currentLabelScales.filter(
      (scale) => (scale.macId || scale.mac_id || "").trim().toUpperCase() !== targetMacId,
    );

    const updatedConfig = {
      ...currentConfig,
      label_scales: filteredScales,
    };

    const updatedProfile = {
      ...currentProfile,
      config: updatedConfig,
    };

    localStorage.setItem(STORAGE_KEYS.SHOP_PROFILE, JSON.stringify(updatedProfile));

    const resp = await ShopService.updateShopProfile({ config: updatedConfig });
    if (resp && typeof resp === "object") {
      localStorage.setItem(STORAGE_KEYS.SHOP_PROFILE, JSON.stringify(resp));
    }
  } catch (error) {
    console.error("Failed to remove machine from shop profile config:", error);
  }
}

export function isNoDiscountSpread() {
  const distribution = getCachedShopProfile()?.config?.discount_distribution;

  return distribution?.trim().toLowerCase() === DISCOUNT_DISTRIBUTION.NO_SPREAD;
}

const ShopsHelper = {
  saveSelectedShopDetails,
  getSubscriptionRemainingDays,
  getAmountDecimalCount,
  getAmountFormatted,
  getBankDetails,
  saveBankDetails,
  getCachedShopProfile,
  getPaymentModes,
  hasFullKotValidity,
  hasKotLiteValidity,
  hasKotValidity,
  isPremiumShop,
  showOrderStatus,
  saveMachineToShopProfileConfig,
  removeMachineFromShopProfileConfig,
  hasLabelScaleValidity,
  isNoDiscountSpread,
};

export default ShopsHelper;
